import { useCallback, useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { ApiError } from '@schemas'
import { Button } from '@/components/ui/button'
import { useSeriesContext } from '@/layouts/SeriesLayout'
import { reportActorKey, useCurrentUser } from '@/api/hooks'
import { SeriesMatchingWorkspace } from '@/components/shows/series-matching-workspace'
import { isSeriesAccessError, purgeRevokedSeriesEditions } from '@/components/shows/series-draft'

export default function SeriesAlignmentPage() {
  const series = useSeriesContext()
  const user = useCurrentUser()
  const qc = useQueryClient()
  const scope = JSON.stringify(reportActorKey(user.data))
  const [activeScope, setActiveScope] = useState(scope)
  const [denied, setDenied] = useState(false)
  const [revoked, setRevoked] = useState<Set<string>>(new Set())
  const revoke = useCallback(
    (ids: string[]) =>
      setRevoked(current =>
        ids.some(id => !current.has(id)) ? new Set([...current, ...ids]) : current
      ),
    []
  )
  useEffect(() => {
    if (revoked.size) purgeRevokedSeriesEditions(qc, revoked)
  }, [qc, revoked])
  const readableEditions = useRef(new Map<string, Set<string>>())
  useEffect(() => {
    if (scope !== activeScope || denied) {
      void qc.cancelQueries({ queryKey: ['show-series'] })
      qc.removeQueries({ queryKey: ['show-series'] })
      if (scope !== activeScope) {
        readableEditions.current.clear()
        setRevoked(new Set())
        setDenied(false)
        setActiveScope(scope)
      }
    }
  }, [scope, activeScope, denied, qc])
  useEffect(
    () =>
      qc.getQueryCache().subscribe(event => {
        if (event.query.queryKey[0] !== 'show-series') return
        if (isSeriesAccessError(event.query.state.error)) {
          const error = event.query.state.error
          const params = event.query.queryKey.at(-1) as { targetShowId?: string } | undefined
          if (
            error instanceof ApiError &&
            error.status === 404 &&
            error.payload?.code === 'SHOW_NOT_FOUND' &&
            event.query.queryKey.includes('suggestions') &&
            params?.targetShowId
          )
            revoke([params.targetShowId])
          else setDenied(true)
        }
        if (
          event.type === 'updated' &&
          event.action.type === 'success' &&
          event.query.queryKey[1] === 'workspace' &&
          ['alignment', 'suggestions', 'negative-evidence', 'matching'].includes(
            String(event.query.queryKey[4])
          )
        ) {
          const value = event.query.state.data as { editions?: { showId: string }[] } | undefined
          if (!value?.editions) return
          const current = new Set(value.editions.map(e => e.showId))
          const previous = readableEditions.current.get(event.query.queryHash)
          if (previous) revoke([...previous].filter(id => !current.has(id)))
          readableEditions.current.set(event.query.queryHash, current)
        }
      }),
    [qc, revoke]
  )
  if (denied)
    return (
      <div className="space-y-3">
        <p role="alert">
          Some series data is no longer available with your current access or membership. Private
          cached data and drafts were cleared.
        </p>
        <Button
          variant="outline"
          onClick={() => {
            readableEditions.current.clear()
            setDenied(false)
          }}
        >
          Reload accessible workspace
        </Button>
      </div>
    )
  if (activeScope !== scope || !user.data) return <p role="status">Checking series access…</p>
  return (
    <SeriesMatchingWorkspace
      key={`${series.id}:${scope}`}
      series={series}
      onDenied={() => setDenied(true)}
    />
  )
}
